import type { UserSettings } from '../services/user.service';

/** Minimal user shape needed to merge audio prefs (matches `AuthUser` fields used). */
export type AudioSettingsUser = {
    userId: number | string;
    guest: boolean;
    settings: UserSettings | null;
};

const DEFAULT_LEVEL = 100;

/**
 * Fields merged for a synthetic `UserSettings` row when the user has no settings yet.
 * `music` / `sfx` match API defaults (100 = full level).
 */
const AUDIO_DEFAULTS: Pick<
    UserSettings,
    'music' | 'sfx' | 'color' | 'titleId' | 'backgroundId' | 'avatarId' | 'colorPaletteId'
> = {
    music: DEFAULT_LEVEL,
    sfx: DEFAULT_LEVEL,
    color: '',
    titleId: null,
    backgroundId: null,
    avatarId: null,
    colorPaletteId: null,
};

/** Coerce patch or stored values to 0–100 (`true`/`false` → 100/0 for legacy callers). */
export function normalizeAudioSettingLevel0to100(value: unknown): number {
    if (typeof value === 'boolean') {
        return value ? DEFAULT_LEVEL : 0;
    }
    const n = Math.round(Number(value));
    if (!Number.isFinite(n)) {
        return DEFAULT_LEVEL;
    }
    return Math.min(100, Math.max(0, n));
}

/** Partial music/sfx update; booleans accepted for backward compatibility. */
export type AudioSettingsPatch = {
    music?: number | boolean;
    sfx?: number | boolean;
};

/**
 * Merge music/sfx into current settings, or build client defaults when `settings` is null.
 * Always returns `music` / `sfx` as integers in 0–100.
 */
export function applyAudioSettingsPatch(user: AudioSettingsUser, patch: AudioSettingsPatch): UserSettings {
    const prev = user.settings;
    if (prev) {
        return {
            ...prev,
            ...(patch.music !== undefined ? { music: normalizeAudioSettingLevel0to100(patch.music) } : {}),
            ...(patch.sfx !== undefined ? { sfx: normalizeAudioSettingLevel0to100(patch.sfx) } : {}),
        };
    }

    const numericUserId = typeof user.userId === 'number' ? user.userId : 0;

    return {
        id: 0,
        userId: numericUserId,
        ...AUDIO_DEFAULTS,
        title: null,
        avatar: null,
        ...(patch.music !== undefined ? { music: normalizeAudioSettingLevel0to100(patch.music) } : {}),
        ...(patch.sfx !== undefined ? { sfx: normalizeAudioSettingLevel0to100(patch.sfx) } : {}),
    };
}
